import type { IndividuoArboreo } from "@/mocks/cta";
import { useNavigate } from "react-router-dom";
import TreeStatusBadge from "./TreeStatusBadge";
import TreeRiskBadge from "./TreeRiskBadge";
import TreeReviewBadge from "./TreeReviewBadge";

interface TreeCardProps {
  individuo: IndividuoArboreo;
}

export default function TreeCard({ individuo }: TreeCardProps) {
  const navigate = useNavigate();
  const riscoBorder = {
    baixo: "border-l-primary-400",
    medio: "border-l-caution-500",
    alto: "border-l-warning-500",
    critico: "border-l-danger-500",
  };

  return (
    <div className={`bg-white rounded-card border border-l-2 ${riscoBorder[individuo.classeRisco]} p-5 hover:shadow-md transition-all duration-200`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-foreground-500 tracking-tight">{individuo.codigoCta}</p>
          <h3 className="text-sm font-semibold text-foreground-900 font-heading mt-0.5">{individuo.nomePopular}</h3>
          <p className="text-xs text-foreground-400 italic">{individuo.nomeCientifico}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <TreeStatusBadge status={individuo.estadoFitossanitario} />
          <TreeRiskBadge risco={individuo.classeRisco} />
        </div>
      </div>

      {/* Info */}
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-map-pin-line text-foreground-400 text-[10px]"></i>
          <span className="truncate">{individuo.logradouro}, {individuo.numero} — {individuo.bairro}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-folder-chart-line text-foreground-400 text-[10px]"></i>
          <span className="truncate">{individuo.projeto}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-file-text-line text-foreground-400 text-[10px]"></i>
          <span>{individuo.tipo} · DAP: {individuo.dap}cm · h: {individuo.altura}m · Ø copa: {individuo.diametroCopa}m</span>
        </div>
      </div>

      {/* Recommendation */}
      <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-background-50">
        <i className="ri-tools-line text-foreground-500 text-xs"></i>
        <span className="text-xs text-foreground-700 flex-1">{individuo.recomendacao}</span>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900">{individuo.dap}</p>
          <p className="text-[10px] text-foreground-500">DAP (cm)</p>
        </div>
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900">{individuo.altura}</p>
          <p className="text-[10px] text-foreground-500">Altura (m)</p>
        </div>
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900">{individuo.diametroCopa}</p>
          <p className="text-[10px] text-foreground-500">Copa (m)</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-calendar-line text-[10px]"></i>
          <span>Vist.: {new Date(individuo.ultimaVistoria).toLocaleDateString("pt-BR")}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-camera-line text-[10px]"></i>
          <span>{individuo.fotos} fotos</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <button onClick={() => navigate(`/cta/${individuo.id}`)} className="btn-primary text-xs px-2 py-1.5 flex-1 cursor-pointer">
          <i className="ri-file-chart-line text-[10px]"></i>
          Ver FCA
        </button>
        <button className="btn-outline text-xs px-2 py-1.5 cursor-pointer" title="Abrir no mapa">
          <i className="ri-map-2-line text-[10px]"></i>
        </button>
        <button className="btn-outline text-xs px-2 py-1.5 cursor-pointer" title="Nova vistoria">
          <i className="ri-clipboard-line text-[10px]"></i>
        </button>
        <button className="btn-outline text-xs px-2 py-1.5 cursor-pointer" title="Fotos">
          <i className="ri-camera-line text-[10px]"></i>
        </button>
      </div>
    </div>
  );
}